import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import { theme } from './theme/theme';
import { OrderProvider, useOrder } from './state/OrderContext';
import { MenuProvider } from './state/MenuContext';
import { LayoutProvider } from './state/LayoutContext';
import { AuthProvider, useAuth } from './state/AuthContext';
import AppHeader from './components/layout/AppHeader';
import BottomNav from './components/layout/BottomNav';
import TablesScreen from './components/screens/TablesScreen';
import OrderScreen from './components/screens/OrderScreen';
import BillScreen from './components/screens/BillScreen';
import KitchenScreen from './components/screens/KitchenScreen';
import AdminScreen from './components/screens/AdminScreen';
import StatsScreen from './components/screens/StatsScreen';
import TenantsScreen from './components/screens/TenantsScreen';
import LoginScreen from './components/screens/LoginScreen';
import PrinterSettings from './components/PrinterSettings';
import { FlameMark } from './assets/FlameMark';

export type Screen = 'tables' | 'order' | 'bill' | 'kitchen' | 'admin' | 'stats' | 'tenants';

const AppContent: React.FC = () => {
  const { state } = useOrder();
  const { logout, isAdmin, canManageTenants } = useAuth();
  const [screen, setScreen] = useState<Screen>('tables');
  const [selectedTable, setSelectedTable] = useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const pendingCount = useMemo(
    () =>
      Object.values(state.tables).reduce(
        (sum, order) => sum + order.items.filter((item) => item.sent && !item.delivered).length,
        0
      ),
    [state.tables]
  );

  const handleSelectTable = (tableNumber: number): void => {
    setSelectedTable(tableNumber);
    setScreen('order');
  };

  const handleBack = (): void => {
    if (screen === 'bill') {
      setScreen('order');
    } else {
      setScreen('tables');
      setSelectedTable(null);
    }
  };

  const handleNavigate = (nextScreen: Screen): void => {
    setScreen(nextScreen);
  };

  const handlePaid = (): void => {
    setScreen('tables');
    setSelectedTable(null);
  };

  const showBottomNav =
    screen === 'tables' || screen === 'kitchen' || screen === 'admin' || screen === 'stats' || screen === 'tenants';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppHeader
        screen={screen}
        tableNumber={selectedTable}
        pendingCount={pendingCount}
        onBack={handleBack}
        onOpenSettings={() => setSettingsOpen(true)}
        onLogout={logout}
      />
      <Box sx={{ flex: 1, overflow: screen === 'order' ? 'hidden' : 'auto', backgroundColor: '#F5F5F5' }}>
        {screen === 'tables' && <TablesScreen onSelectTable={handleSelectTable} />}
        {screen === 'order' && selectedTable !== null && (
          <OrderScreen tableNumber={selectedTable} onOpenBill={() => setScreen('bill')} />
        )}
        {screen === 'bill' && selectedTable !== null && (
          <BillScreen tableNumber={selectedTable} onPaid={handlePaid} />
        )}
        {screen === 'kitchen' && <KitchenScreen />}
        {screen === 'admin' && <AdminScreen />}
        {screen === 'stats' && <StatsScreen />}
        {screen === 'tenants' && <TenantsScreen />}
      </Box>
      {showBottomNav && (
        <BottomNav
          screen={screen}
          pendingCount={pendingCount}
          showAdmin={isAdmin}
          showTenants={canManageTenants}
          onNavigate={handleNavigate}
        />
      )}
      <PrinterSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Box>
  );
};

const SuperadminApp: React.FC = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ gap: 0.5, px: 1 }}>
          <FlameMark sx={{ color: '#fff', fontSize: 28, mx: 1 }} cutoutColor="#B71C1C" />
          <Typography variant="h6" noWrap sx={{ color: '#fff', flexGrow: 1 }}>
            {t('tenants.title')}
          </Typography>
          <IconButton onClick={logout} sx={{ color: '#fff' }}>
            <LogoutRounded />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: '#F5F5F5' }}>
        <TenantsScreen />
      </Box>
    </Box>
  );
};

const AppShell: React.FC = () => {
  const { isAuthenticated, isSuperadmin } = useAuth();

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  if (isSuperadmin) {
    return <SuperadminApp />;
  }

  return (
    <MenuProvider>
      <LayoutProvider>
        <OrderProvider>
          <AppContent />
        </OrderProvider>
      </LayoutProvider>
    </MenuProvider>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
